/**
 * The editors' one palette: the room's default material colors in the 3D
 * view, and the drafting paper and grid both the plan and the 3D view draw.
 *
 * These are the *defaults*, the standard palette of a typical apartment or
 * single-family rental: white ceilings, warm off-white (cream) walls, beige
 * floors, medium warm-brown wood doors, light-stone counters. Per-element
 * overrides may layer on top of these later.
 *
 * Values are linear RGB in [0, 1], matching mesh3d's `RGB`.
 */

/** A color, each channel in 0..1. */
export type RGB = [number, number, number];

/** A color with its opacity, each in 0..1. */
export type RGBA = [number, number, number, number];

/** A `#rrggbb` color as RGB in 0..1. */
export const rgbFromHex = (hexColor: string): RGB => {
  const [r, g, b] = hexColor.match(/\w\w/g)!.map(c => parseInt(c, 16) / 255);
  return [r, g, b];
};

/** A CSS color, for the platforms' SVG and styles: `#rrggbb` for an opaque color, `rgba(r, g, b, a)` for one with its opacity. */
export const cssColor = (color: RGB | RGBA): string => {
  if (color.length === 3) return hexFromRgb(color);
  const [red, green, blue] = color.slice(0, 3).map(channel => Math.round(channel * 255));
  return `rgba(${red}, ${green}, ${blue}, ${color[3]})`;
};

/** RGB in 0..1 as a `#rrggbb` color, each channel clamped to 0..1, for the platforms' SVG. */
export const hexFromRgb = (color: RGB): string =>
  '#' + color.map(channel => Math.round(Math.min(1, Math.max(0, channel)) * 255).toString(16).padStart(2, '0')).join('');

export const PALETTE: {
  ceiling: RGB;
  wall: RGB;
  floor: RGB;
  door: RGB;
  counter: RGB;
  object: RGB;
  accent: RGB;
  outline: RGB;
  stage: RGB;
  gridMinor: RGBA;
  gridMajor: RGBA;
} = {
  ceiling: [0.98, 0.98, 0.98], // white
  wall: rgbFromHex('#f2eee2'), // off-white cream
  floor: rgbFromHex('#dbd1c1'), // beige
  door: rgbFromHex('#867361'), // medium warm-brown wood
  counter: [0.72, 0.72, 0.74], // light stone
  // Free 3-D objects (furniture, appliances, cabinets, fixtures): brand blue.
  object: rgbFromHex('#95d5eb'),
  // Selection highlight: river blue, matching the 2D editor's --accent.
  accent: rgbFromHex('#40879f'),
  // Every outline: charcoal, matching the 2D editor's --room-stroke.
  outline: [0.11, 0.11, 0.11],
  // The drafting paper behind the plan and the 3D view.
  stage: rgbFromHex('#fffdf8'),
  // The drafting grid's lines on that paper, in the plan and the 3D view: a
  // minor line every grid step, a major one every fifth, both in ink.
  gridMinor: [26 / 255, 26 / 255, 26 / 255, 0.06],
  gridMajor: [26 / 255, 26 / 255, 26 / 255, 0.13],
};
